export const WEB_ACCESS = Object.freeze({
  ADMIN: 'admin',
  PLATFORM_ADMIN: 'platform_admin',
  LEADER: 'leader',
  SECURITY_ADMIN: 'security_admin',
  AUDITOR: 'auditor',
  PLATFORM_SUPPORT: 'platform_support',
  OPERATIONAL: 'operational',
  NONE: 'none',
});

export const MOBILE_APP_ONLY_MESSAGE =
  'This account is for field operations. Please use the DisasterMS mobile app.';

const normalize = (value) => String(value || '').trim().toLowerCase();

const userRoles = (user) => {
  const rawRole = user?.role;
  const roles = Array.isArray(rawRole) ? rawRole : [rawRole];
  return roles.map(normalize).filter(Boolean);
};

const isPlatformAdministrator = (user) => {
  const roles = [
    ...(user?.saas_context?.platform_roles || []),
    ...(user?.platform_roles || []),
    ...(Array.isArray(user?.role) ? user.role : [user?.role]),
  ].map(normalize);
  return roles.some((role) => ['saas_superadmin', 'superadmin', 'super_admin'].includes(role));
};

/**
 * The backend owns the web-access decision. Role checks remain only as a
 * compatibility path for existing administrator records.
 */
export const getWebAccessLevel = (user) => {
  if (!user || typeof user !== 'object') return WEB_ACCESS.NONE;

  const declaredAccess = normalize(user.web_access);
  const roles = userRoles(user);
  const isCompatibleAdmin = roles.some((role) => role === 'admin');
  const declaredSupportSession = declaredAccess === WEB_ACCESS.PLATFORM_SUPPORT
    && Boolean(user?.saas_context?.institution_id)
    && Boolean(user?.saas_context?.support_grant_id);

  // A support-only token deliberately originates from a platform identity.
  // If an old cached response still carries that source role, the signed
  // support context and backend-declared access mode remain authoritative.
  if (declaredSupportSession) {
    return WEB_ACCESS.PLATFORM_SUPPORT;
  }

  if (declaredAccess === WEB_ACCESS.PLATFORM_ADMIN || isPlatformAdministrator(user)) {
    return WEB_ACCESS.PLATFORM_ADMIN;
  }

  if (declaredAccess === WEB_ACCESS.ADMIN || user.is_admin === true || isCompatibleAdmin) {
    return WEB_ACCESS.ADMIN;
  }

  if (declaredAccess === WEB_ACCESS.LEADER && user.is_explicit_leader === true) {
    return WEB_ACCESS.LEADER;
  }

  if ([WEB_ACCESS.SECURITY_ADMIN, WEB_ACCESS.AUDITOR, WEB_ACCESS.PLATFORM_SUPPORT, WEB_ACCESS.OPERATIONAL].includes(declaredAccess)) {
    return declaredAccess;
  }

  return WEB_ACCESS.NONE;
};

export const isWebAdmin = (user) => getWebAccessLevel(user) === WEB_ACCESS.ADMIN;

export const hasWebAccess = (user) => getWebAccessLevel(user) !== WEB_ACCESS.NONE;

export const hasActiveSupportInstitutionSession = (user) => Boolean(
  user?.saas_context?.institution_id && user?.saas_context?.support_grant_id
);

const LEADER_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/notifications\/?$/,
  /^\/profile\/?$/,
  /^\/exercise-map\/[^/]+\/?$/,
  /^\/incident-details\/[^/]+(?:\/chat)?\/?$/,
];

const SECURITY_ADMIN_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/notifications\/?$/,
  /^\/profile\/?$/,
  /^\/users\/?$/,
  /^\/access-administration\/?$/,
];

const AUDITOR_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/notifications\/?$/,
  /^\/profile\/?$/,
  /^\/reports\/?$/,
  /^\/incident-details\/[^/]+\/?$/,
];

const PLATFORM_SUPPORT_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/profile\/?$/,
  /^\/support-access\/?$/,
];

const OPERATIONAL_ROUTE_PATTERNS = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/notifications\/?$/,
  /^\/profile\/?$/,
  /^\/exercise-map\/[^/]+\/?$/,
  /^\/incident-details\/[^/]+(?:\/chat)?\/?$/,
  /^\/scenario-library(?:\/[^/]+)?\/?$/,
  /^\/scenario-marketplace\/?$/,
];

export const canAccessWebPath = (user, pathname = '/', search = '') => {
  const access = getWebAccessLevel(user);
  if (access === WEB_ACCESS.ADMIN) return true;

  const normalizedPath = String(pathname || '/').split('?')[0] || '/';
  if (access === WEB_ACCESS.PLATFORM_ADMIN) {
    return /^\/saas-administration\/?$/.test(normalizedPath)
      || /^\/notifications\/?$/.test(normalizedPath)
      || /^\/profile\/?$/.test(normalizedPath);
  }
  if (access === WEB_ACCESS.SECURITY_ADMIN) {
    return SECURITY_ADMIN_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
  }
  if (access === WEB_ACCESS.AUDITOR) {
    return AUDITOR_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
  }
  if (access === WEB_ACCESS.PLATFORM_SUPPORT) {
    if (hasActiveSupportInstitutionSession(user) && (
      /^\/reports\/?$/.test(normalizedPath)
      || /^\/notifications\/?$/.test(normalizedPath)
      || /^\/incident-details\/[^/]+\/?$/.test(normalizedPath)
    )) return true;
    return PLATFORM_SUPPORT_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
  }
  if (access === WEB_ACCESS.OPERATIONAL) {
    if (/^\/meets\/?$/.test(normalizedPath)) {
      const params = new URLSearchParams(search || '');
      return Boolean(params.get('incidentId') && params.get('callId') && params.get('meeting_id'));
    }
    return OPERATIONAL_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
  }
  if (access !== WEB_ACCESS.LEADER) return false;

  if (/^\/incident-details\/[^/]+(?:\/chat)?\/?$/.test(normalizedPath)) {
    const params = new URLSearchParams(search || '');
    if (params.get('scenario') === '1' || params.has('sid')) return false;
  }

  if (/^\/meets\/?$/.test(normalizedPath)) {
    const params = new URLSearchParams(search || '');
    return Boolean(
      params.get('incidentId')
      && params.get('callId')
      && params.get('meeting_id')
    );
  }

  return LEADER_ROUTE_PATTERNS.some((pattern) => pattern.test(normalizedPath));
};

export const extractAuthUser = (response) => {
  if (!response || response?.status === false) return null;
  if (response?.data?.user) return response.data.user;
  if (response?.data && typeof response.data === 'object') return response.data;
  if (response?.user) return response.user;
  return typeof response === 'object' ? response : null;
};

export const clearWebSession = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('user');
  localStorage.removeItem('impersonating');
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
  localStorage.removeItem('active_institution');
  localStorage.removeItem('support_session');
  localStorage.removeItem('platform_session_backup');
  window.dispatchEvent(new Event('impersonation-changed'));
};
